import useHomeTiara from '@/root/hooks/useHomeTiara';
import usePopup, {PapasErrorPopupProps} from '@/root/hooks/usePopup';

import Popup from '@/shared/components/Popup/Layout';
import {DicaronIssueAndHomeErrorCode} from '@/shared/constants/dicaronServerErrorCode';
import {TIARA_ACTION, TIARA_HOME_ACTION} from '@/shared/constants/tiara';
import useHistoryBackAOS from '@/shared/hooks/useHistoryBackAOS';
import {nl2br} from '@/shared/utils/nl2br';

type ErrorInfo = {
  [key in DicaronIssueAndHomeErrorCode]: {title: string; content: JSX.Element[] | string};
};

const ERROR_INFO: ErrorInfo = {
  [DicaronIssueAndHomeErrorCode.FOREIGNER]: {
    title: '발급을 진행할 수 없어요.',
    content: nl2br('국내 전화번호로 인증된 카카오톡으로만 이용할 수 있습니다.')
  },
  [DicaronIssueAndHomeErrorCode.PAPAS_MAINTENANCE]: {
    title: '발급을 진행할 수 없어요.',
    content: nl2br('현재 서비스 점검으로 새로운 발급이 불가합니다.\n잠시 후 다시 시도해 주세요.')
  },
  [DicaronIssueAndHomeErrorCode.MAINTENANCE]: {
    title: '발급을 진행할 수 없어요.',
    content: nl2br('현재 서비스 점검으로 새로운 발급이 불가합니다.\n잠시 후 다시 시도해 주세요.')
  },
  [DicaronIssueAndHomeErrorCode.PAPAS_NOT_APPLICABLE]: {
    title: '발급을 진행할 수 없어요.',
    content: nl2br('카드 발급 대상이 아닙니다.\n자세한 사항은 고객센터로 문의해 주세요.')
  },
  [DicaronIssueAndHomeErrorCode.PAPAS_TOO_MANY_REQUEST]: {
    title: '증명서 발급 지연 안내',
    content: nl2br('요청이 증가하여 서비스 이용이 지연되고 있습니다.\n잠시 후 다시 시도해 주세요.')
  },
  [DicaronIssueAndHomeErrorCode.TOO_MANY_REQUESTS]: {
    title: '증명서 발급 지연 안내',
    content: nl2br('요청이 증가하여 서비스 이용이 지연되고 있습니다.\n잠시 후 다시 시도해 주세요.')
  },
  [DicaronIssueAndHomeErrorCode.UNDER_14]: {
    title: '발급을 진행할 수 없어요.',
    content: '14세 미만은 카드 발급이 불가합니다.'
  },
  [DicaronIssueAndHomeErrorCode.UNKNOWN]: {
    title: '서비스 오류 안내',
    content: nl2br(
      '예상하지 못한 오류가 발생하였습니다.\n서버의 일시적인 장애이거나 네트워크 문제일 수 있습니다. 잠시 후 다시 시도해 주세요.'
    )
  }
};

const PapasErrorPopup = ({errorCode, onSubmit}: PapasErrorPopupProps) => {
  const isTooManyRequest =
    errorCode === DicaronIssueAndHomeErrorCode.PAPAS_TOO_MANY_REQUEST ||
    errorCode === DicaronIssueAndHomeErrorCode.TOO_MANY_REQUESTS;
  const isMaintenance = errorCode === DicaronIssueAndHomeErrorCode.PAPAS_MAINTENANCE;
  const pageView = isTooManyRequest
    ? TIARA_HOME_ACTION.TOO_MANY_REQUEST
    : isMaintenance
    ? TIARA_HOME_ACTION.PAPAS_MAINTENANCE
    : undefined;

  const {trackEvent} = useHomeTiara({pageView});
  const {closePopup} = usePopup();

  const onConfirm = () => {
    const action = isTooManyRequest ? TIARA_HOME_ACTION.TOO_MANY_REQUEST_CONFIRM_CLICK : TIARA_ACTION.CONFIRM_CLICK;
    trackEvent(action);
    closePopup();
    onSubmit?.();
  };

  useHistoryBackAOS(() => {
    onConfirm();
  }, []);

  const {title, content} = ERROR_INFO[errorCode] ?? ERROR_INFO.UNKNOWN;

  return (
    <Popup>
      <Popup.Title>{title}</Popup.Title>
      <Popup.Content>{content}</Popup.Content>
      <Popup.GroupButton>
        <button type="button" className="btn btn_gray" onClick={onConfirm}>
          확인
        </button>
      </Popup.GroupButton>
    </Popup>
  );
};

export default PapasErrorPopup;
